import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  getDocs,
  doc,
  updateDoc,
  getDoc,
  where,
  serverTimestamp,
  addDoc,
  deleteDoc
} from 'firebase/firestore';
import { db, storage, getAdminEmails } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from './AuthContext';
import { Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const AdminContext = createContext();

export function useAdmin() {
  return useContext(AdminContext);
}

export function AdminProvider({ children }) {
  const { currentUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  
  // New state for categorized orders
  const [ordersReceived, setOrdersReceived] = useState([]);
  const [revisionsRequested, setRevisionsRequested] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);

  // Enhanced admin status check with mounted check
  useEffect(() => {
    let isMounted = true;
    
    const checkAdminStatus = async () => {
      if (!currentUser) {
        if (isMounted) {
          setIsAdmin(false);
          setLoading(false);
        }
        return;
      }

      if (isMounted) {
        setLoading(true);
      }

      try {
        const adminEmails = getAdminEmails();
        console.group('Admin Status Check');
        console.log('Starting admin status verification');
        console.log('Current User:', currentUser ? currentUser.email : 'No user');
        console.log('Node Environment:', process.env.REACT_APP_ENVIRONMENT);
        console.log('Is Emulator:', process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true');
        console.log('Configured Admin Emails:', adminEmails);
        console.log('Current User Email:', currentUser.email);
    
        const HARDCODED_ADMIN_EMAILS = [
            'jonrosenfield@me.com', 
            'daniel.i.rosenfield@gmail.com'
        ];
    
        if (
            adminEmails.includes(currentUser.email.toLowerCase()) || 
            HARDCODED_ADMIN_EMAILS.includes(currentUser.email.toLowerCase())
        ) {
            console.log('Admin status confirmed by email');
            if (isMounted) {
                setIsAdmin(true);
                setLoading(false);
            }
            return;
        }

        // Fetch user document only if not an admin by email
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        console.log('User Document Fetch Attempt:', { uid: currentUser.uid, exists: userDoc.exists() });
        console.log('User Document Data:', userDoc.exists() ? userDoc.data() : 'No document found');
        
        if (userDoc.exists() && userDoc.data().isAdmin === true) {
          console.log('Admin status confirmed by Firestore document');
          if (isMounted) {
            setIsAdmin(true);
            setLoading(false);
          }
          return;
        }

        console.log('No admin privileges found');
        if (isMounted) {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Admin status check error:', {
          message: error.message,
          code: error.code,
          stack: error.stack,
        });
        // Gracefully handle permission error for non-admins
        if (isMounted) {
          setIsAdmin(false); // Default to non-admin if fetch fails
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
        console.groupEnd();
      }
    };

    checkAdminStatus();

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  // Categorize orders by status
  const categorizeOrders = (ordersList) => {
    const newOrdersReceived = ordersList.filter(order => order.status === 'Order Received');
    const newRevisionsRequested = ordersList.filter(order => order.status === 'Revision Requested');
    const newCompletedOrders = ordersList.filter(order => order.status === 'Completed');
    
    setOrdersReceived(newOrdersReceived);
    setRevisionsRequested(newRevisionsRequested);
    setCompletedOrders(newCompletedOrders);
    
    return {
      ordersReceived: newOrdersReceived,
      revisionsRequested: newRevisionsRequested,
      completedOrders: newCompletedOrders
    };
  };

  // Optimized fetchOrders to prevent unnecessary fetches
  const fetchOrders = async () => {
    console.log('Attempting to fetch orders', { isAdmin, currentUser });
    
    if (!isAdmin || !currentUser) {
      console.log('Not authorized to fetch orders');
      return [];
    }
  
    try {
      const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const ordersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
  
      console.log('Processed orders list:', ordersList);
      
      // Fetch user data for each order
      const uniqueUserIds = [...new Set(ordersList.map(order => order.userId).filter(Boolean))];
      console.log('Unique user IDs:', uniqueUserIds);
      
      // Fetch all unique users in parallel
      const userDataMap = {};
      await Promise.all(
        uniqueUserIds.map(async (userId) => {
          try {
            const userData = await fetchUserById(userId);
            userDataMap[userId] = userData;
          } catch (error) {
            console.error(`Error fetching user data for userId ${userId}:`, error);
            userDataMap[userId] = { name: 'Unknown User' };
          }
        })
      );
      
      // Attach user data to each order
      const ordersWithUserData = ordersList.map(order => {
        if (order.userId && userDataMap[order.userId]) {
          return { ...order, userData: userDataMap[order.userId] };
        }
        return { ...order, userData: { name: 'Unknown User' } };
      });
  
      setOrders(ordersWithUserData);
      applyFilter(filter, ordersWithUserData);
      categorizeOrders(ordersWithUserData);
      return ordersWithUserData;
    } catch (error) {
      console.error('Error fetching orders:', error);
      return [];
    }
  };

  // Filter orders
  const applyFilter = (filterType, ordersList = orders) => {
    setFilter(filterType);
    const filteredList = filterType === 'all' 
      ? ordersList 
      : ordersList.filter(order => order.status === filterType);
    setFilteredOrders(filteredList);
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus, statusNote = '', additionalData = {}) => {
    if (!isAdmin) throw new Error('Unauthorized attempt');

    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }
      
      const orderData = orderSnap.data();
      
      // Prepare update data
      const updateData = {
        status: newStatus,
        statusUpdatedAt: serverTimestamp(),
        statusNote: statusNote || ''
      };
      
      // Handle revisions separately if they exist in additionalData
      if (additionalData.revisions) {
        // Clean any potential Timestamp objects in the existing revisions
        const currentRevisions = orderData.revisions || [];
        const cleanedCurrentRevisions = currentRevisions.map(revision => {
          const cleanedRevision = { ...revision };
          
          // Convert timestamp if it's a Timestamp
          if (revision.timestamp && typeof revision.timestamp.toDate === 'function') {
            cleanedRevision.timestamp = revision.timestamp.toDate().toISOString();
          }
          
          return cleanedRevision;
        });
        
        // Clean any potential Timestamp objects in the new revision
        const newRevisions = additionalData.revisions.map(revision => {
          const cleanedRevision = { ...revision };
          
          // Ensure timestamp is a string, not a Timestamp
          if (revision.timestamp && typeof revision.timestamp !== 'string') {
            if (typeof revision.timestamp.toDate === 'function') {
              cleanedRevision.timestamp = revision.timestamp.toDate().toISOString();
            } else {
              cleanedRevision.timestamp = new Date().toISOString();
            }
          }
          
          return cleanedRevision;
        });
        
        // Combine the cleaned current revisions with the new revisions
        updateData.revisions = [...cleanedCurrentRevisions, ...newRevisions];
        
        // Remove revisions from additionalData to avoid double-processing
        const { revisions, ...restAdditionalData } = additionalData;
        Object.assign(updateData, restAdditionalData);
      } else {
        // Add any remaining additional data
        Object.assign(updateData, additionalData);
      }
      
      console.log('Updating order status with data:', updateData);
      
      // First update non-timestamp fields and arrays
      const basicUpdates = { ...updateData };
      delete basicUpdates.statusUpdatedAt; // Remove serverTimestamp fields
      
      await updateDoc(orderRef, basicUpdates);
      
      // Then update timestamp fields separately
      await updateDoc(orderRef, {
        statusUpdatedAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });

      // Update local state
      const updatedOrders = orders.map(order => 
        order.id === orderId ? { 
          ...order, 
          ...basicUpdates,
          statusUpdatedAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        } : order
      );
      
      setOrders(updatedOrders);
      applyFilter(filter, updatedOrders);
      categorizeOrders(updatedOrders);
      return true;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  };

  // Updated uploadSong to separate array and timestamp updates
  const uploadSong = async (orderId, file, metadata = {}) => {
    if (!isAdmin) throw new Error('Unauthorized upload attempt');
    if (!file) throw new Error('No file provided');
  
    try {
      // Detailed diagnostic logging
      console.log('Upload Song Diagnostics:', {
        port: window.location.port,
        environment: process.env.REACT_APP_ENVIRONMENT || 'development',
        orderId,
        fileName: file.name,
        isAdmin,
        currentUser: currentUser ? currentUser.email : 'No user',
        metadata,
        isEmulator: process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true'
      });

      // Step 1: Upload file to storage
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const songRef = ref(storage, `songs/${orderId}/${fileName}`);
      
      console.log(`Uploading file to ${songRef.fullPath}`);
      await uploadBytes(songRef, file);
      console.log('File uploaded successfully, getting download URL');
      const downloadURL = await getDownloadURL(songRef);
      
      // Step 2: Get the current order data
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }
      
      const orderData = orderSnap.data();
      console.log(`Retrieved order data for ${orderId}:`, orderData);

      // Step 3: Clean existing songs array to ensure all timestamps are strings
      const currentSongs = Array.isArray(orderData.songs) ? orderData.songs : [];
      const cleanedSongs = currentSongs.map(song => {
        const cleanedSong = { ...song };
        
        // Convert any Timestamp to ISO string
        if (cleanedSong.uploadedAt && typeof cleanedSong.uploadedAt.toDate === 'function') {
          cleanedSong.uploadedAt = cleanedSong.uploadedAt.toDate().toISOString();
        } else if (typeof cleanedSong.uploadedAt !== 'string') {
          cleanedSong.uploadedAt = new Date().toISOString();
        }
        
        return cleanedSong;
      });
      
      // Step 4: Create song object with string timestamp
      const currentTimestamp = new Date().toISOString();
      const songObject = {
        url: downloadURL,
        fileName: fileName,
        uploadedAt: currentTimestamp,
        name: metadata.name || file.name,
        description: metadata.description || '',
        isMainVersion: !orderData.songUrl
      };
      
      console.log('Created song object:', { ...songObject, url: '[REDACTED]' });
      
      // Step 5: Prepare updates without timestamps
      const updateData = {
        songs: [...cleanedSongs, songObject]
      };
      
      // Update main song fields if needed
      if (!orderData.songUrl || metadata.isMainVersion) {
        updateData.songUrl = downloadURL;
        updateData.songFileName = fileName;
        
        if (orderData.status !== 'Completed') {
          updateData.status = 'Completed';
        }
      }
      
      console.log('Update Data (without timestamps):', updateData);
      
      // Step 6: First update without timestamps
      await updateDoc(orderRef, updateData);
      
      // Step 7: Separate timestamp update
      const timestampUpdate = {
        lastUpdated: serverTimestamp()
      };
      
      // Add timestamp for song upload if it's the first or main version
      if (!orderData.songUrl || metadata.isMainVersion) {
        timestampUpdate.songUploadedAt = serverTimestamp();
        
        if (orderData.status !== 'Completed') {
          timestampUpdate.completedAt = serverTimestamp();
        }
      }
      
      console.log('Timestamp Update:', timestampUpdate);
      
      // Step 8: Update timestamps separately
      await updateDoc(orderRef, timestampUpdate);
      
      // Step 9: Update local state
      const updatedOrders = orders.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              ...updateData,
              lastUpdated: new Date().toISOString(),
              songUploadedAt: timestampUpdate.songUploadedAt ? new Date().toISOString() : order.songUploadedAt,
              completedAt: timestampUpdate.completedAt ? new Date().toISOString() : order.completedAt
            } 
          : order
      );
      
      setOrders(updatedOrders);
      categorizeOrders(updatedOrders);
      
      console.log('Song upload completed successfully');
      return { success: true, url: downloadURL };
    } catch (error) {
      console.error('Upload Error:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  };

  // Set main song version
  const setMainSongVersion = async (orderId, songIndex) => {
    if (!isAdmin) throw new Error('Unauthorized attempt');
    
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) throw new Error('Order not found');
      
      const orderData = orderSnap.data();
      if (!Array.isArray(orderData.songs) || songIndex >= orderData.songs.length) {
        throw new Error('Invalid song index');
      }
      
      const selectedSong = orderData.songs[songIndex];
      
      // Clean existing songs array to ensure all timestamps are strings
      const cleanedSongs = orderData.songs.map((song, index) => {
        const cleanedSong = { ...song };
        
        // Convert any Timestamp objects to ISO strings
        if (song.uploadedAt && typeof song.uploadedAt.toDate === 'function') {
          cleanedSong.uploadedAt = song.uploadedAt.toDate().toISOString();
        }
        
        // Update isMainVersion flag
        cleanedSong.isMainVersion = (index === songIndex);
        
        return cleanedSong;
      });
      
      // Update the main document fields
      const basicUpdate = {
        songUrl: selectedSong.url,
        songFileName: selectedSong.fileName,
        songs: cleanedSongs
      };
      
      await updateDoc(orderRef, basicUpdate);
      
      // Update timestamp fields separately
      await updateDoc(orderRef, {
        lastUpdated: serverTimestamp()
      });
      
      // Update local state
      const updatedOrders = orders.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              songUrl: selectedSong.url,
              songFileName: selectedSong.fileName,
              songs: cleanedSongs,
              lastUpdated: new Date().toISOString()
            } 
          : order
      );
      
      setOrders(updatedOrders);
      categorizeOrders(updatedOrders);
      
      return true;
    } catch (error) {
      console.error('Error setting main song version:', error);
      throw error;
    }
  };

  // Update order lyrics
  const updateOrderLyrics = async (orderId, lyrics) => {
    if (!isAdmin) throw new Error('Unauthorized access attempt');
    if (!orderId) throw new Error('No order ID provided');
    
    try {
      const orderRef = doc(db, 'orders', orderId);
      
      // Update lyrics first (no serverTimestamp)
      await updateDoc(orderRef, {
        lyrics: lyrics
      });
      
      // Update timestamp fields separately
      await updateDoc(orderRef, {
        lyricsUpdatedAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      });
      
      // Update local state
      const updatedOrders = orders.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              lyrics: lyrics,
              lyricsUpdatedAt: new Date().toISOString(),
              lastUpdated: new Date().toISOString()
            } 
          : order
      );
      
      setOrders(updatedOrders);
      applyFilter(filter, updatedOrders);
      categorizeOrders(updatedOrders);
      
      return true;
    } catch (error) {
      console.error('Error updating lyrics:', error);
      throw error;
    }
  };

  // Fetch user by ID
  const fetchUserById = async (userId) => {
    if (!isAdmin) throw new Error('Unauthorized access attempt');
    if (!userId) throw new Error('No user ID provided');
    
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        console.log('User data found for userId:', userId, userSnap.data());
        return { id: userSnap.id, ...userSnap.data() };
      }
      console.warn(`User not found for ID: ${userId}, returning fallback data`);
      return { id: userId, email: 'Unknown User', firstName: 'Unknown', lastName: 'User', isAdmin: false }; // Fallback
    } catch (error) {
      console.error('Detailed error fetching user:', {
        userId,
        message: error.message,
        stack: error.stack,
      });
      throw error;
    }
  };

  // Delete order function - Updated to directly delete from Firestore
  const deleteOrder = async (orderId) => {
    if (!isAdmin) throw new Error('Unauthorized access attempt');
    if (!orderId) throw new Error('No order ID provided');
    
    try {
      console.log(`Attempting to delete order: ${orderId}`);
      
      // Delete directly from Firestore
      await deleteDoc(doc(db, 'orders', orderId));
      
      // Update local state by removing the deleted order
      const updatedOrders = orders.filter(order => order.id !== orderId);
      setOrders(updatedOrders);
      
      // Update filtered orders as well
      applyFilter(filter, updatedOrders);
      categorizeOrders(updatedOrders);
      
      console.log('Order deleted successfully');
      return { success: true, message: 'Order deleted successfully' };
    } catch (error) {
      console.error('Error deleting order:', error);
      // Enhanced error reporting
      if (error.code) {
        console.error(`Error code: ${error.code}`);
      }
      if (error.details) {
        console.error(`Error details:`, error.details);
      }
      throw error;
    }
  };

  // Search orders function with enhanced field coverage
  const searchOrders = (searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) return [];
    
    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return orders.filter(order => {
      // Search through common order fields
      if (order.orderNumber && order.orderNumber.toString().toLowerCase().includes(lowerSearchTerm)) return true;
      if (order.recipientName && order.recipientName.toLowerCase().includes(lowerSearchTerm)) return true;
      if (order.userId && order.userId.toLowerCase().includes(lowerSearchTerm)) return true;
      if (order.package && order.package.toLowerCase().includes(lowerSearchTerm)) return true;
      if (order.status && order.status.toLowerCase().includes(lowerSearchTerm)) return true;
      
      // Search through user data fields if available
      if (order.userData) {
        if (order.userData.name && order.userData.name.toLowerCase().includes(lowerSearchTerm)) return true;
        if (order.userData.email && order.userData.email.toLowerCase().includes(lowerSearchTerm)) return true;
        if (order.userData.firstName && order.userData.firstName.toLowerCase().includes(lowerSearchTerm)) return true;
        if (order.userData.lastName && order.userData.lastName.toLowerCase().includes(lowerSearchTerm)) return true;
      }
      
      // Search through occasion
      if (order.occasion && order.occasion.toLowerCase().includes(lowerSearchTerm)) return true;
      
      // Search through genres if it's an array
      if (Array.isArray(order.genres) && order.genres.some(genre => 
        genre.toLowerCase().includes(lowerSearchTerm)
      )) return true;
      
      // No match found
      return false;
    });
  };

  const value = {
    isAdmin,
    loading,
    orders,
    filteredOrders,
    filter,
    ordersReceived,
    revisionsRequested,
    completedOrders,
    fetchOrders,
    applyFilter,
    updateOrderStatus,
    uploadSong,
    setMainSongVersion,
    fetchUserById,
    updateOrderLyrics,
    deleteOrder,
    searchOrders,
    currentUser
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export default AdminContext;